import React, { useState, useEffect } from "react";
import apiClient from "../../../../../api/apiClient";
import Loading from "../../../../../components/Loading/Loading";
import AudienceBuilder from "./components/AudienceBuilder";
import SequenceBuilder from "./components/SequenceBuilder";
import SendConfig from "./components/SendConfig";
import SafetyRules from "./components/SafetyRules";

const defaultState = () => ({
    name: "",
    subject: "",
    audienceFilters: { statuses: [], priorities: [], sources: [], stageIds: [], assignedTo: "" },
    audienceType: "dynamic",
    steps: [],
    sendConfig: { sendImmediately: true, scheduledAt: null, timeWindowOnly: false },
    safetyRules: { stopOnReply: true, excludeLost: true, skipContactedDays: null },
});

const CampaignBuilder = ({ campaignId, stages, admins, onSave, onCancel }) => {
    const [form, setForm]         = useState(defaultState());
    const [loading, setLoading]   = useState(!!campaignId);
    const [saving, setSaving]     = useState(false);
    const [error, setError]       = useState("");
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [campaignStatus, setCampaignStatus] = useState("draft");
    
    useEffect(() => {
        if (!campaignId) return;
        setLoading(true);
        apiClient.get(`/admin/crm/campaigns/${campaignId}`)
            .then((res) => {
                const { campaign, steps } = res.data;
                setCampaignStatus(campaign.status);
                setIsReadOnly(campaign.status === "active");
                setForm({
                    name: campaign.name,
                    subject: campaign.subject || "",
                    audienceFilters: campaign.audienceFilters || {},
                    audienceType: campaign.audienceType || "dynamic",
                    steps: steps.map((s) => ({
                        type: s.type,
                        body: s.body || "",
                        delayDays: s.delayDays || 1,
                    })),
                    sendConfig: {
                        sendImmediately: !!campaign.sendImmediately,
                        scheduledAt: campaign.scheduledAt || null,
                        timeWindowOnly: !!campaign.timeWindowOnly,
                    },
                    safetyRules: {
                        stopOnReply: !!campaign.stopOnReply,
                        excludeLost: !!campaign.excludeLost,
                        skipContactedDays: campaign.skipContactedDays || null,
                    },
                });
            })
            .catch(() => setError("Failed to load campaign"))
            .finally(() => setLoading(false));
    }, [campaignId]);

    const allowedStages = stages;

    const buildPayload = () => ({
        name: form.name.trim(),
        subject: form.subject.trim(),
        audienceFilters: form.audienceFilters,
        audienceType: form.audienceType,
        steps: form.steps,
        sendImmediately: form.sendConfig.sendImmediately,
        scheduledAt: form.sendConfig.scheduledAt || null,
        timeWindowOnly: form.sendConfig.timeWindowOnly,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        stopOnReply: form.safetyRules.stopOnReply,
        excludeLost: form.safetyRules.excludeLost,
        skipContactedDays: form.safetyRules.skipContactedDays || null,
    });

    const validate = () => {
        if (!form.name.trim()) return "Campaign name is required";
        if (!form.subject.trim()) return "Email subject is required";
        if (form.steps.length === 0) return "Add at least one step";
        if (form.steps[0]?.type === "thread") return "First step cannot be a thread break";
        let stepNum = 0;
        for (let i = 0; i < form.steps.length; i++) {
            const s = form.steps[i];
            if (s.type === "thread") {
                if (!s.subject?.trim()) return `Thread break: subject is required`;
            } else {
                stepNum++;
                if (s.type === "email" && !s.body?.trim()) return `Step ${stepNum}: body is required`;
                if (s.type === "delay" && !(s.delayDays >= 1)) return `Step ${stepNum}: delay must be ≥ 1 day`;
            }
        }
        return null;
    };

    const handleSave = async (launch = false) => {
        const err = validate();
        if (err) { setError(err); return; }
        setError("");
        setSaving(true);
        try {
            let id = campaignId;
            if (!id) {
                const res = await apiClient.post("/admin/crm/campaigns", buildPayload());
                id = res.data.id;
            } else {
                await apiClient.put(`/admin/crm/campaigns/${id}`, buildPayload());
            }
            if (launch) {
                await apiClient.post(`/admin/crm/campaigns/${id}/start`);
            }
            onSave(id);
        } catch (e) {
            setError(e.response?.data?.error || e.response?.data?.errors?.[0]?.msg || "Failed to save campaign");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="admin-loading-wrapper"><Loading /></div>;

    return (
        <div className="crm-builder-layout">

            {/* ── Back navigation ── */}
            <button className="crm-builder-back" onClick={onCancel} type="button">
                ← Campaigns
            </button>

            {/* ── Campaign name ── */}
            <div className="crm-builder-title-row">
                <div className="crm-builder-title-row__heading">
                    {campaignId ? "Edit Campaign" : "New Campaign"}
                </div>
                <input
                    className="crm-builder-name-input"
                    placeholder="Give your campaign a name…"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={isReadOnly}
                />
            </div>

            {isReadOnly && (
                <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)", marginBottom: "var(--space-5)", fontSize: "var(--text-sm)", color: "#92400e" }}>
                    This campaign is currently active — pause it to edit the sequence.
                </div>
            )}

            {/* ── Two-column layout ── */}
            <div className="crm-builder-grid">

                {/* ── Sidebar: Audience + Safety + Send ── */}
                <div className="crm-builder-sidebar">

                    {/* Audience */}
                    <div className="crm-sidebar-section">
                        <div className="crm-sidebar-section__title">Audience</div>
                        <AudienceBuilder
                            filters={form.audienceFilters}
                            audienceType={form.audienceType}
                            onFiltersChange={(v) => setForm((f) => ({ ...f, audienceFilters: v }))}
                            onAudienceTypeChange={(v) => setForm((f) => ({ ...f, audienceType: v }))}
                            stages={allowedStages}
                            admins={admins}
                            safetyRules={form.safetyRules}
                        />
                    </div>

                    {/* Safety */}
                    <div className="crm-sidebar-section">
                        <div className="crm-sidebar-section__title">Safety Rules</div>
                        <SafetyRules
                            rules={form.safetyRules}
                            onChange={(v) => setForm((f) => ({ ...f, safetyRules: v }))}
                        />
                    </div>

                    {/* Sending */}
                    <div className="crm-sidebar-section">
                        <div className="crm-sidebar-section__title">Sending</div>
                        <SendConfig
                            config={form.sendConfig}
                            onChange={(v) => setForm((f) => ({ ...f, sendConfig: v }))}
                        />
                    </div>

                </div>

                {/* ── Main: Sequence ── */}
                <div className="crm-builder-main">
                    <div className="crm-builder-section-heading">
                        <span className="crm-builder-section-heading__title">Email Sequence</span>
                        {form.steps.length > 0 && (
                            <span className="crm-builder-section-heading__meta">
                                {form.steps.length} step{form.steps.length !== 1 ? "s" : ""}
                            </span>
                        )}
                    </div>

                    {isReadOnly ? (
                        <div className="crm-sequence-groups">
                            {(() => {
                                // Build groups for read-only display
                                const groups = [];
                                let current = { threadSubject: form.subject, steps: [] };
                                form.steps.forEach((s) => {
                                    if (s.type === "thread") { groups.push(current); current = { threadSubject: s.subject, steps: [] }; }
                                    else current.steps.push(s);
                                });
                                groups.push(current);
                                let stepNum = 0;
                                return groups.map((g, gi) => (
                                    <div key={gi} className={`crm-thread-group crm-thread-group--readonly${gi === 0 ? " crm-thread-group--first" : ""}`}>
                                        <div className="crm-thread-group__header">
                                            <span className="crm-thread-group__label">{["1st","2nd","3rd"][gi] || `${gi+1}th`} Thread</span>
                                            <span className="crm-thread-group__subject-display">{g.threadSubject}</span>
                                        </div>
                                        <div className="crm-thread-group__body">
                                            <div className="crm-sequence-timeline">
                                                {g.steps.map((s, si) => {
                                                    stepNum++;
                                                    return (
                                                        <div key={si} className="crm-step-timeline-item">
                                                            <div className="crm-step-indicator">
                                                                <div className={`crm-step-indicator__circle crm-step-indicator__circle--${s.type}`}>{stepNum}</div>
                                                                {si < g.steps.length - 1 && <div className="crm-step-indicator__line" />}
                                                            </div>
                                                            <div className={`crm-step-card crm-step-card--${s.type}`} style={{ cursor: "default" }}>
                                                                <div className="crm-step-card__header">
                                                                    <span className="crm-step-card__title">
                                                                        {s.type === "email" ? "Email" : `Wait ${s.delayDays} day${s.delayDays !== 1 ? "s" : ""}`}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>
                    ) : (
                        <SequenceBuilder
                            steps={form.steps}
                            onChange={(v) => setForm((f) => ({ ...f, steps: v }))}
                            subject={form.subject}
                            onSubjectChange={(v) => setForm((f) => ({ ...f, subject: v }))}
                        />
                    )}
                </div>
            </div>

            {/* ── Footer ── */}
            {!isReadOnly && (
                <div className="crm-builder-footer">
                    <button className="admin-btn" onClick={onCancel} disabled={saving} type="button">
                        Close
                    </button>
                    {campaignStatus === "paused" ? (
                        <>
                            <button
                                className="admin-btn"
                                onClick={async () => {
                                    setSaving(true);
                                    try {
                                        await apiClient.post(`/admin/crm/campaigns/${campaignId}/cancel`);
                                        setCampaignStatus("draft");
                                    } catch (e) {
                                        setError(e?.response?.data?.error || "Failed to cancel campaign");
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                                disabled={saving}
                                type="button"
                                style={{ color: "#dc2626", borderColor: "#dc2626" }}
                            >
                                Cancel Campaign
                            </button>
                            <button className="admin-btn admin-btn--primary" onClick={() => handleSave(false)} disabled={saving} type="button">
                                {saving ? <Loading /> : "Update Campaign"}
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="admin-btn admin-btn--primary" onClick={() => handleSave(false)} disabled={saving} type="button">
                                {saving ? <Loading /> : "Save as Draft"}
                            </button>
                            <button
                                className="admin-btn admin-btn--primary"
                                onClick={() => handleSave(true)}
                                disabled={saving}
                                type="button"
                                style={{ background: "#10b981", borderColor: "#10b981" }}
                            >
                                {saving ? <Loading /> : "Save & Launch ✓"}
                            </button>
                        </>
                    )}
                    {error && <span className="admin-form-error">{error}</span>}
                </div>
            )}
        </div>
    );
};

export default CampaignBuilder;
